import type { IntlShape } from 'react-intl'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import type { RedeemedCard } from './common'
import config from 'constants/config'

const LE_CARD_NUMBER_LENGTH = 16
const BLACK_HAWK_SPEND_LIMIT_PER_ORDER = 1500

export const isBlackhwakCard = (code: string): boolean => {
  if (code.length === LE_CARD_NUMBER_LENGTH && code.match(/[0-9]{16}/)) {
    return true
  }
  return code.length > LE_CARD_NUMBER_LENGTH
}

export const validateGiftCard = (redeemedCards: Array<RedeemedCard>, redeemedCard: RedeemedCard, payable: number, intl: IntlShape): void => {
  if (isDuplicateGiftCard(redeemedCards, redeemedCard)) {
    throw new Error(intl.formatMessage({
      id: 'Sp0PEpVSN69-RQDQwTnk',
      defaultMessage: 'This gift card has already been redeemed.',
      description: 'Error message when a duplicate gift card is entered during redemption',
    }))
  }

  if (isBlackhawkLimitReached(redeemedCards, redeemedCard)) {
    const regionCode = config.regions.find(region => region.currencyCode === redeemedCard.currencyCode)?.code || 'AU'
    throw new Error(intl.formatMessage({
      id: '1aKFuHG4CxZKMdfL6nEV',
      defaultMessage: 'Maximum redemption value of {formattedLimit} for physical Gift Card reached',
      description: 'Error message when the maximum physical gift card redemption limit is reached',
    }, {
      formattedLimit: formatCurrency(BLACK_HAWK_SPEND_LIMIT_PER_ORDER, redeemedCard.currencyCode, regionCode),
    }))
  }

  if (isRedeemedHigherThanPayable(redeemedCards, payable)) {
    throw new Error(intl.formatMessage({
      id: 'M8F8r0dF8DRYynT1IMNR',
      defaultMessage: 'Cannot redeem once gift card value is higher than payable amount.',
      description: 'Error message when gift card value exceeds the remaining payable amount',
    }))
  }
}

const isDuplicateGiftCard = (redeemedCards: Array<RedeemedCard>, redeemedCard: RedeemedCard): boolean => {
  return redeemedCards.some(card => card.code === redeemedCard.code)
}

const isRedeemedHigherThanPayable = (redeemedCards: Array<RedeemedCard>, payable: number): boolean => {
  return redeemedCards.reduce((acc, card) => acc + card.amount, 0) >= payable
}

const isBlackhawkLimitReached = (redeemedCards: Array<RedeemedCard>, redeemedCard: RedeemedCard): boolean => {
  const redeemedCardAmount = isBlackhwakCard(redeemedCard.code) ? redeemedCard.amount : 0
  const pinCardsTotal = redeemedCards
    .filter(card => isBlackhwakCard(card.code))
    .reduce((acc, card) => acc + card.amount, 0)

  return pinCardsTotal + redeemedCardAmount > BLACK_HAWK_SPEND_LIMIT_PER_ORDER
}
